import { useAsyncData } from '#imports'

import type {
  BasePageContent,
  MappedApiPageContent,
} from '@backmarket/http-api/src/api-specs-content/models/base-page-content'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import { useFormatContentResponse } from './useFormatContentResponse'

export type ContentPagePath =
  | '/content/page/:pageType/:pageName'
  | '/content/page/:pageType/:pageName/:pageCategory'

export type FormattedPageContentEndpoint = ContentPagePath

export type FormattedPageContentPathParams = {
  pageType: string
  pageName: string
  pageCategory?: string
}

/**
 * @returns T with all keys of O overridden by the type of O
 */
type Override<T, O extends object> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends Record<any, any> ? Omit<T, keyof O> & O : O

type FormattedPageContentBaseOptions<DataT> = NonNullable<
  Parameters<typeof useAsyncData<DataT>>[2]
>

export function useFetchFormattedPageContent<
  DataT extends MappedApiPageContent<BasePageContent> =
    MappedApiPageContent<BasePageContent>,
>(
  path: ContentPagePath,
  options: Override<
    FormattedPageContentBaseOptions<DataT>,
    {
      pathParams: FormattedPageContentPathParams
    }
  >,
) {
  const $httpFetch = useHttpFetch()
  const formatContentResponse = useFormatContentResponse()
  const { pathParams, ...asyncDataOptions } = options
  const key = getAsyncDataKey('cms-formatted-page-content', pathParams)

  return useAsyncData(
    key,
    () =>
      $httpFetch(path, {
        pathParams: {
          pageType: pathParams.pageType,
          pageName: pathParams.pageName,
          pageCategory: pathParams.pageCategory || '',
        },
      }),
    {
      ...asyncDataOptions,
      transform: formatContentResponse,
    },
  )
}
